import { LoadingService } from 'src/app/shared/services';
import { UniversalSearchModel } from '../../../core/models/universal-search.model';
import { Injectable } from '@angular/core';
import { RequestBodyContact } from '../../../core/models/request-body.model';
import { CriteriaToRequestService } from '../../../shared/services/criteria-to-request.service';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { AccountAttributesEnum, SidebarTabTypeEnum } from 'src/app/shared/enums';
import { BaseApiService, UniversalApiService } from '../../../shared/services/api';
import { SearchModelForm } from './SearchModelForm';
import { FormBuilder } from '@angular/forms';
import { UserService } from 'src/app/core/services/user.service';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage } from '../../../shared/helpers';
import { SearchCountDto } from '../../../shared/dto';
import { SliderRange } from '../../../shared/components/ui-components/range-slider/range-slider.component';
import { IcpIntelModelService } from '../../sidebar/services/icp-intel-model.service';
import { KeyValueModel } from '../../../shared/models';

export interface ShowRepeatPageInterface {
    show: boolean,
    message?: string,
}
export const UNIVERSAL_SEARCH_COUNTER_LOADING_TAG: string = 'universal-search-counter-loading-tag';
export enum IndustryFilterType {
    NAME = 'showIndusName',
    CODE = 'showCode',
}

@Injectable({
    providedIn: 'root',
})
export class SearchService {
    universalSearchURL = 'service/contactcloud/universal';
    industrySearchType: IndustryFilterType = IndustryFilterType.NAME;
    public changeSelectedTab: BehaviorSubject<'COMPANIES' | 'CONTACTS'> = new BehaviorSubject(null);
    public universalCount;
    public isLoaderHidden: boolean = false;
    private assistedSearchText: string = null;
    private companyDomainChipText: string[] = [];
    private searchCriteriaInner;
    private selectedTabInner: 'COMPANIES' | 'CONTACTS';
    private viewCompanyContacts = new BehaviorSubject<boolean>(null);
    private isSearchInProgressSubject = new Subject<boolean>();
    private clearSearchSubject = new Subject<void>();
    private sidebarSubmitTrigger = new Subject<void>();
    searchModelDataWrapper: SearchModelForm;
    readonly isSearchListRepeatPageShown$: Observable<ShowRepeatPageInterface>;
    private readonly isSearchListRepeatPageShownSubject: BehaviorSubject<ShowRepeatPageInterface>
        = new BehaviorSubject<ShowRepeatPageInterface>({ show: false });
    private companyRetryEvent = new Subject<void>();
    private contactRetryEvent = new Subject<void>();
    private searchPerformed: {
        [key in SidebarTabTypeEnum]?: {subject: Subject<boolean>, state: boolean}
    } = {
            [SidebarTabTypeEnum.SALES_INTEL]: {subject: new Subject<boolean>(), state: false},
            [SidebarTabTypeEnum.VISITOR_INTEL]: {subject: new Subject<boolean>(), state: false},
    };

    private accuracyTierUpdated = new Subject<void>();
    private icpIntelCompanyScoreUpdated = new Subject<SliderRange>();

    savedSearchModelData: any;

    constructor(
        public criteriaToRequestService: CriteriaToRequestService,
        private readonly baseApiService: BaseApiService,
        private fb: FormBuilder,
        private userService: UserService,
        private readonly universalApiService: UniversalApiService,
        private readonly loadingService: LoadingService,
        private readonly icpIntelModelService: IcpIntelModelService
    ) {
        this.isSearchListRepeatPageShown$ = this.isSearchListRepeatPageShownSubject.asObservable();
        this.searchModelDataWrapper = new SearchModelForm(this.fb);
    }


    set searchCriteria(val) {
        this.searchCriteriaInner = val;
    }

    get searchCriteria() {
        return this.searchCriteriaInner;
    }

    set selectedTab(val) {
        this.selectedTabInner = val;
    }

    get selectedTab() {
        return this.selectedTabInner;
    }

    set searchModelData(val: any) {
      this.searchModelDataWrapper.patchValue({...val,
         ...(this.userService.hasAccess(AccountAttributesEnum.INTERNATIONAL)
          ? {is_international: null}
          : {is_international: 0}),
        });
    }

    get searchModelData() {
      return this.searchModelDataWrapper;
    }

    set searchTextInput(value: string) {
        this.assistedSearchText = value;
    }

    get searchTextInput(): string {
        return this.assistedSearchText;
    }

    set chipTextInput(value: string[]) {
        this.companyDomainChipText = value;
    }

    get chipTextInput(): string[] {
        return this.companyDomainChipText;
    }

    postUniversalSearchCount(requestBody: any): Observable<SearchCountDto> {
        return this.baseApiService.post<SearchCountDto>(`${this.universalSearchURL}/count`, this.criteriaToRequest(requestBody), { showError: true });
    }

    getUniversalSearchCount(requestBody): Observable<SearchCountDto | HttpErrorResponse> {
        this.setSearchListRepeatPageShown(false);

        return this.loadingService.loadingWrapper(
            this.postUniversalSearchCount(requestBody),
            UNIVERSAL_SEARCH_COUNTER_LOADING_TAG,
        ).pipe(catchError((error: HttpErrorResponse) => this.handleRepeatError({ ...error, status: 400 }, true)));
    }

    getCompanies(requestBody): Observable<any> {
        this.searchCriteria = requestBody;
        this.setSearchListRepeatPageShown(false);

        return this.baseApiService.post(`${this.universalSearchURL}/search`, this.criteriaToRequest(requestBody))
            .pipe(catchError((error: HttpErrorResponse) => this.handleRepeatError(error)));
    }

    emitAccuracyTierUpdated(): void {
        this.accuracyTierUpdated.next();
    }

    getAccuracyTierUpdated(): Observable<void> {
        return this.accuracyTierUpdated.asObservable();
    }

    emitSidebarSubmitTrigger(): void {
      this.sidebarSubmitTrigger.next();
    }

    getSidebarSubmitTrigger(): Observable<void> {
      return this.sidebarSubmitTrigger.asObservable();
    }

    emitIcpIntelCompanyScoreUpdated(data: SliderRange): void {
          this.icpIntelCompanyScoreUpdated.next(data);
    }

    getIcpIntelCompanyScoreUpdated(): Observable<SliderRange> {
          return this.icpIntelCompanyScoreUpdated.asObservable();
    }

    setIcpIntelModel(item: KeyValueModel): void {
      this.icpIntelModelService.setIcpIntelModel(item);
    }

    getIcpIntelModel(key: string): Observable<KeyValueModel> {
      return this.icpIntelModelService.getIcpIntelModel(key);
    }

    setViewCompanyContacts(flag: boolean) {
        this.viewCompanyContacts.next(flag);
    }

    getViewCompanyContacts(): Observable<boolean> {
        return this.viewCompanyContacts.asObservable();
    }

    updateSearchInProgress(value: boolean) {
        this.isSearchInProgressSubject.next(value);
    }

    getSearchInProgress() {
        return this.isSearchInProgressSubject.asObservable();
    }

    clearSearch(): void {
        this.setSearchListRepeatPageShown(false);
        this.clearSearchSubject.next();
    }

    getClearSearch(): Observable<void> {
        return this.clearSearchSubject.asObservable();
    }

    getCompanyRetryEvent(): Observable<void> {
        return this.companyRetryEvent.asObservable();
    }
    getContactRetryEvent(): Observable<void> {
        return this.contactRetryEvent.asObservable();
    }
    emitCompanyRetryEvent() {
        this.companyRetryEvent.next();
    }
    emitContactRetryEvent() {
        this.contactRetryEvent.next();
    }

    getSearchPerformedEvent(panel: SidebarTabTypeEnum): Observable<boolean> {
        return this.searchPerformed[panel].subject.asObservable();
    }

    getSearchPerformedState(panel: SidebarTabTypeEnum): boolean {
        return this.searchPerformed[panel].state;
    }

    emitSearchPerformedEvent(isSearch: boolean, panel: SidebarTabTypeEnum) {
        this.searchPerformed[panel].state = isSearch;
        this.searchPerformed[panel].subject.next(isSearch);
    }

    getContacts(requestBody): Observable<any> {
        this.searchCriteria = requestBody;
        let requestPreview;
        this.setDefaultSort(requestBody);
        requestPreview = this.criteriaToRequest(requestBody);
        this.setSearchListRepeatPageShown(false);

        return this.baseApiService.post(`${this.universalSearchURL}/search`, requestPreview)
            .pipe(catchError((error: HttpErrorResponse) => this.handleRepeatError(error)));
    }

    exportAllContactsMatchsId(requestBodyContact, hasAccuracyTiersAccess?): Observable<any> {
        return this.baseApiService.post('service/contactcloud/universal/matchIds' + (hasAccuracyTiersAccess ? 'AccuracyTiers' : ''), this.criteriaToRequest(requestBodyContact), { showError: true });
    }

    exportAllContactsMatchIdsV2WithoutCriteriaToRequest(requestBodyContact): Observable<any> {
        return this.universalApiService.getMatchIdsV2(requestBodyContact);
    }

    exportAllContactsMatchIdsV2(requestBodyContact): Observable<any> {
        return this.universalApiService.getMatchIdsV2(this.criteriaToRequest(requestBodyContact));
    }

    setDefaultSort(requestBody: RequestBodyContact) {
        if (!requestBody.sort_by || requestBody.sort_by === '') {
            requestBody.sort_by = 'contact_score';
        }
    }

    getCompanyIds(requestBody): Observable<any> {
        return this.baseApiService.post(`${this.universalSearchURL}/companyIds`, this.criteriaToRequest(requestBody), { showError: true });
    }

    exportAllListMatchsId(requestBodyContact): Promise<any> {
        return this.baseApiService.post('service/contactcloud/list/matchIds', requestBodyContact).toPromise();
    }

    criteriaToRequest(criteria: any): any {
        const request = {};
        request['request_type'] = criteria['request_type'];
        this.criteriaToRequestService.transformCopy(criteria, request, [
            'keyword',
            'company_location_metro',
            'contact_location_metro', // universal contact location update
            'page',
            'sort_by',
            'page_size',
            'sort_direction',
            'company_location_zipcodes_distance',
            'contact_revenue_min_identifier', 'contact_revenue_max_identifier',
            'company_technographic_sub_categories',
            'specialties',
            'specialties_any',
            'specialties_all',
            'specialties_none',
            'popular_keywords',
            'gte_last_modified_date', 'max_result_per_company', 'job_start_date',
            'si_intent_topic_ids', 'intent_topic_ids', 'intent_minimum_score', 'intent_maximum_score', 'intent_topic_threshold',
            'page_filters',
            'owned_contacts_company',
            'owned_contacts_person',
            'company_technographic_products_bool_tab', 'company_technographic_vendors_bool_tab', 'company_technographic_sub_categories_bool_tab',
            'company_location_states',
            'department_size',
            'include_primary_industry_only',
            'viDateFrom',
            'viDateTo',
            'viSearchDurationType',
            'viUUID',
            'it_spend_super_category',
            'it_spend_category',
            'min_it_spend_amount',
            'max_it_spend_amount',
            'last_funding_rounds',
            'min_funding_amount',
            'max_funding_amount',
            'last_funding_date',
            'min_last_funding_date',
            'max_last_funding_date',
            'is_intent_automation_from_email'
        ]);
        this.criteriaToRequestService.transformRankings(criteria, request, 'rankings');
        this.criteriaToRequestService.transformJobTitle(criteria, request, ['job_title', 'job_title_any', 'job_title_all', 'job_title_none']);
        this.criteriaToRequestService.transformArrayToCSV(criteria, request, ['contact_location_zipcodes']);
        this.criteriaToRequestService.transDefaultValueCountry(
            criteria,
            request,
            'location_country_codes'
        );
        this.criteriaToRequestService.transformArrayToCSV(criteria, request, ['match_ids']);
        this.criteriaToRequestService.transformTiersCsv(criteria, request, 'tiers');
        this.criteriaToRequestService.transformMapArrayToArray(criteria, request, 'company_ids', 'companyIds');
        this.criteriaToRequestService.transformStringToArray(criteria, request, [
            'company_location_zipcodes', 'company_domains', 'company_names', 'company_naics_codes', 'company_naics_codes_any', 'company_naics_codes_all',
            'company_naics_codes_none', 'company_sic_codes', 'company_sic_codes_any', 'company_sic_codes_all', 'company_sic_codes_none',
            'it_spend_sub_category'
        ]);
        this.criteriaToRequestService.transformArray(criteria, request, ['lists', 'suppression_lists', 'location_types', 'contact_lists', 'rodId']);
        this.criteriaToRequestService.transformIfNotNull(criteria, request, [
            'first_name', 'last_name', 'email', 'emails',
            'matchOnJobHistory',
            'company_location_hq',
            'company_location_branch',
            'contact_location_zipcodes_distance',
            'company_score_min', 'company_score_max', 'icpintel_job_uuid'
        ]);
        if (criteria.hasOwnProperty('contact_location_states')) {
            this.criteriaToRequestService.transformArrayToString(
                criteria,
                request,
                'contact_location_states',
            );
        }
        this.criteriaToRequestService.transformCopyWithZero(criteria, request, [
            'company_min_revenue', 'company_max_revenue',
            'company_min_size', 'company_max_size',
            'verified',
        ]);
        this.criteriaToRequestService.transformCopyWithNull(criteria, request, ['is_international']);
        this.criteriaToRequestService.transformBooleanArraytoArray(criteria, request,
            ['company_technographic_products_bool', 'company_technographic_vendors_bool', 'company_technographic_sub_categories_bool']);
        this.criteriaToRequestService.transformArraytoArray(criteria, request,
            ['company_technographic_products', 'company_technographic_vendors'], 'value');
        this.criteriaToRequestService.transformRequiredFieldsCsv(criteria, request, 'required_fields');
        this.criteriaToRequestService.transformTruthMapCsv(criteria, request, ['job_levels', 'job_departments']);
        this.criteriaToRequestService.transformArrayToArray(criteria, request, 'job_sub_departments');
        this.criteriaToRequestService.transformAccuracyTiers(criteria, request, 'accuracy_tier_contact', 'accuracy_tier_company');

        this.criteriaToRequestService.adjustLocationProperties(request);
        this.criteriaToRequestService.adjustFundingProperties(criteria, request);
        return request;
    }

    resetSearchModelData() {
        this.searchModelData = cloneDeep(UniversalSearchModel);
    }

    saveSearchModelData(data): void {
        this.savedSearchModelData = cloneDeep(data);
    }

    setSearchListRepeatPageShown(show: boolean, message?: string): void {
        this.isSearchListRepeatPageShownSubject.next({ show, message });
    }

    private handleRepeatError(error: HttpErrorResponse, checkFourHundred: boolean = false): Observable<HttpErrorResponse> {
        if (error.status !== 403) {
            const message: string = checkFourHundred && error.status === 400 ? getErrorMessage(error) : null;
            this.updateSearchInProgress(false);
            this.setSearchListRepeatPageShown(true, message);
        } else {
            this.setSearchListRepeatPageShown(false, null);
            this.isLoaderHidden = true;
        }
        return throwError(error);
    }
}
